import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { FaArrowLeft } from "react-icons/fa";
import { useGetDetail } from "../../../hooks/useGetDetail";
import formatDate from "../../../utils/formatDate";

const SaleReturnDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data, loading } = useGetDetail("v1/sale-returns", id);

  const detail = Array.isArray(data) ? data?.[0] : data;

  const getStatusChip = (status) => {
    const colors = {
      Processed: { bgcolor: "#4caf50", color: "#fff" },
      Pending: { bgcolor: "#ff9800", color: "#fff" },
      Cancelled: { bgcolor: "#f44336", color: "#fff" },
    };
    const style = colors[status] || { bgcolor: "#9e9e9e", color: "#fff" };
    return (
      <Chip
        label={status || "N/A"}
        size="small"
        sx={{ ...style, fontWeight: 500, fontSize: "12px" }}
      />
    );
  };

  const items = Array.isArray(detail?.items) ? detail.items : [];

  const getItemName = (item) =>
    item?.item_id?.name ||
    item?.name ||
    item?.medicine ||
    item?.itemName ||
    "N/A";

  const getPurchaseQty = (item) =>
    item?.purchaseQty ?? item?.originalQty ?? item?.purchasedQty ?? item?.qty ?? 0;

  const getPreviouslyReturned = (item) =>
    item?.previouslyReturned ??
    item?.previousReturnQty ??
    item?.prevReturned ??
    0;

  const getReturnedQty = (item) =>
    item?.returnedQty ?? item?.returnQty ?? item?.refundQty ?? item?.qty ?? 0;

  const getRemainingQty = (item) => item?.remainingQty ?? item?.remaining ?? 0;

  const getUnitPrice = (item) => item?.unitPrice ?? item?.price ?? 0;

  const getRefundPerUnit = (item) =>
    item?.refundPerUnit ?? item?.refundUnitPrice ?? item?.refundAmount ?? 0;

  const getLineTotal = (item) =>
    item?.lineReturnAmount ??
    item?.lineTotal ??
    item?.total ??
    getReturnedQty(item) * getRefundPerUnit(item);

  const totalReturnAmount =
    detail?.totalReturnAmount ??
    detail?.totalRefundAmount ??
    items.reduce((sum, item) => sum + (Number(getLineTotal(item)) || 0), 0);

  return (
    <>
      <div className="content-header">
        <div className="container-fluid px-2">
          <div className="row mb-2">
            <div className="col-sm-12">
              <ol className="breadcrumb">
                <li className="breadcrumb-item active">Sale Return Details</li>
              </ol>
            </div>
          </div>
        </div>
      </div>

      <section className="content" style={{ paddingBottom: "1.5rem" }}>
        <div className="container-fluid px-4">
          <div className="px-3 py-2">
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 2,
              }}
            >
              <Typography
                variant="h5"
                sx={{ fontWeight: 600, fontSize: "20px" }}
              >
                Sale Return Details
              </Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={<FaArrowLeft size={16} />}
                onClick={() => navigate("/sale-return")}
              >
                Back to List
              </Button>
            </Box>

            {loading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  flexDirection: "column",
                  gap: 2,
                  py: 8,
                }}
              >
                <CircularProgress size={30} sx={{ color: "#1976d2" }} />
                <Typography variant="body2" color="text.secondary">
                  Loading sale return details...
                </Typography>
              </Box>
            ) : (
              <>
                <Paper
                  elevation={0}
                  sx={{
                    mb: 3,
                    p: 3,
                    bgcolor: "#e3f2fd",
                    border: "1px solid #bbdefb",
                    borderRadius: 2,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      flexWrap: "wrap",
                      gap: 3,
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 280 }}>
                      <InfoItem
                        label="Return Code"
                        value={detail?.code || "N/A"}
                      />
                      <InfoItem
                        label="Original Voucher"
                        value={
                          detail?.relatedVoucher?.code ||
                          detail?.voucherCode ||
                          "N/A"
                        }
                      />
                      <InfoItem
                        label="Customer"
                        value={
                          detail?.relatedPatient?.name ||
                          detail?.customerName ||
                          "N/A"
                        }
                      />
                      <InfoItem
                        label="Reason"
                        value={detail?.reason || "N/A"}
                      />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 280 }}>
                      <InfoItem
                        label="Status"
                        valueNode={getStatusChip(detail?.status)}
                      />
                      <InfoItem
                        label="Return Date"
                        value={
                          detail?.returnDate
                            ? formatDate(detail.returnDate)
                            : detail?.createdAt
                              ? formatDate(detail.createdAt)
                              : "N/A"
                        }
                      />
                      <InfoItem
                        label="Payment"
                        value={detail?.paymentMethod || "N/A"}
                      />
                    </Box>
                  </Box>
                </Paper>

                <TableContainer
                  component={Paper}
                  sx={{
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                    borderRadius: 2,
                  }}
                >
                  <Table sx={{ minWidth: 900 }} size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                        <TableCell sx={{ fontWeight: 600, width: 40 }}>
                          #
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Medicine</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Purchase Qty
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Previously Returned
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Returned Qty
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Remaining Qty
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Unit Price
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Refund/Unit
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Line Total
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {items.length > 0 ? (
                        items.map((item, index) => (
                          <TableRow
                            key={item._id || index}
                            sx={{
                              "&:hover": { backgroundColor: "#f5f5f5" },
                              "&:last-child td, &:last-child th": {
                                border: 0,
                              },
                            }}
                          >
                            <TableCell>{index + 1}</TableCell>
                            <TableCell
                              sx={{ fontSize: "13px", fontWeight: 500 }}
                            >
                              {getItemName(item)}
                            </TableCell>
                            <TableCell sx={{ fontSize: "13px" }}>
                              {getPurchaseQty(item)}
                            </TableCell>
                            <TableCell sx={{ fontSize: "13px" }}>
                              {getPreviouslyReturned(item)}
                            </TableCell>
                            <TableCell sx={{ fontSize: "13px" }}>
                              {getReturnedQty(item)}
                            </TableCell>
                            <TableCell sx={{ fontSize: "13px" }}>
                              {getRemainingQty(item)}
                            </TableCell>
                            <TableCell sx={{ fontSize: "13px" }}>
                              {Number(getUnitPrice(item)).toLocaleString()}
                            </TableCell>
                            <TableCell sx={{ fontSize: "13px" }}>
                              {Number(getRefundPerUnit(item)).toLocaleString()}
                            </TableCell>
                            <TableCell
                              sx={{ fontSize: "13px", fontWeight: 600 }}
                            >
                              {Number(getLineTotal(item)).toLocaleString()}
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell
                            colSpan={9}
                            align="center"
                            sx={{ py: 4, color: "#888" }}
                          >
                            No items found.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Box
                  sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      px: 3,
                      py: 1.5,
                      bgcolor: "#e8f5e9",
                      border: "1px solid #c8e6c9",
                      borderRadius: 2,
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 600,
                        color: "#2e7d32",
                        fontSize: "14px",
                      }}
                    >
                      Total Return Amount
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        color: "#2e7d32",
                        fontSize: "18px",
                      }}
                    >
                      {Number(totalReturnAmount || 0).toLocaleString()}
                    </Typography>
                  </Paper>
                </Box>
              </>
            )}
          </div>
        </div>
      </section>
    </>
  );
};

const InfoItem = ({ label, value, valueNode }) => (
  <Box sx={{ mb: 1.5 }}>
    <Typography
      variant="body2"
      sx={{ color: "#1976d2", fontWeight: 600, fontSize: "12px" }}
    >
      {label}
    </Typography>
    {valueNode || (
      <Typography
        variant="body1"
        sx={{ fontWeight: 500, fontSize: "14px", color: "#1a237e" }}
      >
        {value || "N/A"}
      </Typography>
    )}
  </Box>
);

export default SaleReturnDetail;